import { Link } from 'react-router'
import { ExternalLink, FolderUp, LogIn, LogOut, Shield, ShieldCheck, Upload, User as UserIcon } from 'lucide-react'

import { useAuth } from '@/hooks/use-auth'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'

export function Avatar({ className }: { className?: string }) {
  const { user } = useAuth()
  const src = user?.avatarUrl || user?.picture
  const initial = (user?.name || user?.email || '?').charAt(0).toUpperCase()
  return src ? (
    <img src={src} alt="" referrerPolicy="no-referrer" className={cn('size-8 rounded-full object-cover ring-2 ring-primary/20', className)} />
  ) : (
    <span className={cn('flex size-8 items-center justify-center rounded-full bg-brand-gradient text-sm font-semibold text-white', className)}>
      {initial}
    </span>
  )
}

export function UserMenu({ compact = false }: { compact?: boolean }) {
  const { user, isLoading, isModerator, isAdmin, login, logout } = useAuth()

  if (isLoading) return <span className="size-8 animate-pulse rounded-full bg-muted" aria-hidden />

  if (!user) {
    return compact ? (
      <Button variant="ghost" size="icon" onClick={login} aria-label="Sign in">
        <LogIn />
      </Button>
    ) : (
      <Button size="sm" onClick={login}>
        <LogIn />
        Sign in
      </Button>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="rounded-full outline-offset-2" aria-label="Account">
        <Avatar />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="flex items-center gap-3">
          <Avatar className="size-10" />
          <span className="min-w-0">
            <span className="block truncate font-medium">{user.name}</span>
            <span className="block truncate text-xs text-muted-foreground">{user.email}</span>
          </span>
        </DropdownMenuLabel>
        {isModerator && (
          <div className="mx-2.5 mb-1 inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
            <Shield className="size-3" /> Moderator
          </div>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/upload">
            <Upload /> Upload
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/me">
            <FolderUp /> My uploads
          </Link>
        </DropdownMenuItem>
        {isAdmin && (
          <DropdownMenuItem asChild>
            <Link to="/admin">
              <ShieldCheck /> Admin
            </Link>
          </DropdownMenuItem>
        )}
        <DropdownMenuItem asChild>
          <a href="https://auth.ahri.cloud/user/dash" target="_blank" rel="noopener noreferrer">
            <UserIcon /> Hirax account <ExternalLink className="ml-auto opacity-60" />
          </a>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={logout}>
          <LogOut /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
